import classNames from 'classnames'
import * as css from '@modules/order/stores/view/List/./ListViewTableService.css'
import ViewSettingStore from '@modules/order/stores/ViewSettingStore'
import NoContent from '@modules/order/components/CellTemplates/NoContent'
import withAlignment, {
  Alignment
} from '@components/UI-Components/HierarchyWiseItemsTable/components/Utils/Alignment'
import { VerticalAlignment } from '@components/UI-Components/HierarchyWiseItemsTable/components/Utils/Alignment/Alignment'
import {
  Column,
  ColumnType
} from '@components/UI-Components/HierarchyWiseItemsTable/models/column'
import HeaderCell from '@components/UI-Components/HierarchyWiseItemsTable/components/Utils/HeaderCell'
import withCustomField from '@modules/order/components/CellTemplates/CustomFieldHOC'
import { VIPChannel } from '@modules/common/models/enums/VIPChannel'
import QuantityHeaderWithKPI from '../../components/EnhancedListView/EnhancedCellTemplates/EnhancedQuantitiesWithKPI/QuantityHeaderWithKPI'
import {
  getQuantityKPIColumns,
  getQuantityKPILabels
} from '@modules/order/constants/QuantityHeaderKPI'
import { config } from '@config'
import { ASSORTMENT_SOURCE } from '@constants'
import { groupBy, sortBy } from 'lodash'
import EnhancedListHierarchyHeader from '../../components/EnhancedListView/EnhancedCellTemplates/EnhancedListHierarchyHeader'
import CNFProductInfo from '@modules/order/components/CellTemplates/ProductInfo/CNFProductInfo'
import IAProductQuantityCell from '../../components/EnhancedListView/IACellTemplates/IAProductQuantityCell'
import IAProductHeirarchyDetailsCell from '../../components/EnhancedListView/IACellTemplates/IAProductHeirarchyDetailsCell'
import IADoorQuantityCell from '../../components/EnhancedListView/IACellTemplates/IADoorQuantityCell'
import IADoorLevelQuantitiesCell from '../../components/EnhancedListView/IACellTemplates/IADoorLevelQuantitiesCell'
import IADoorLevelHierarchyCell from '../../components/EnhancedListView/IACellTemplates/IADoorLevelHierarchyCell'
import IASingleValueHeirarchyDetailsCell from '../../components/EnhancedListView/IACellTemplates/IASingleValueHeirarchyDetailsCell'
import IAWarehouseHeaderWithKPI from '../../components/EnhancedListView/IACellTemplates/IAWarehouseHeaderWithKPI'
import IAWarehouseQuantitiesCell from '../../components/EnhancedListView/IACellTemplates/IAWarehouseQuantitiesCell'

const commonColumnWidth = 150
export default class InitialAllocationListViewTableService extends ViewSettingStore {
  commonGroupCellClass = classNames(css.groupedCell, 'listViewTableGroupCellOverride')

  getClusteredDoors(doorOrders: any[], quantityKPIS: any[], hideClusterNameRow) {
    if (hideClusterNameRow) {
      return sortBy(doorOrders, ['door.sortOrder', 'door.name']).map(doorOrder =>
        this.getColumnForDoor(doorOrder, quantityKPIS, true)
      ) as Column[]
    } else {
      let groupedDoors = groupBy(doorOrders, 'door.cluster.id')
      return Object.keys(groupedDoors)
        .sort(
          (clusterId1, clusterId2) =>
            groupedDoors[clusterId1][0].door.cluster.sortOrder -
            groupedDoors[clusterId2][0].door.cluster.sortOrder
        )
        .map(clusterId => ({
          field: clusterId,
          label: groupedDoors[clusterId][0].door.cluster.name || 'Unclustered',
          filterSubColumns: true,
          showHeader: true,
          allowHide: true,
          showBorders: true,
          allowReordering: true,
          type: ColumnType.Grouped,
          columns: groupedDoors[clusterId]
            .sort(
              (doorOrder1, doorOrder2) =>
                doorOrder1.door.sortOrder - doorOrder2.door.sortOrder
            )
            .map(doorOrder => this.getColumnForDoor(doorOrder, quantityKPIS, true)),
          cellElement: NoContent
        })) as Column[]
    }
  }

  getColumnForDoor(
    doorOrder: any,
    quantityKPIS: any[],
    allowReordering: boolean,
    doorOrdersLength?
  ) {
    return {
      field: doorOrder.door.id,
      label: doorOrder.door.name,
      width: 150,
      allowHide: true,
      showBorders: false,
      allowReordering,
      initialAllocationField: doorOrder.door.id,
      headerCellElement: withAlignment(
        QuantityHeaderWithKPI({ headers: quantityKPIS, doorOrderId: doorOrder.door.id }),
        Alignment.LEFT,
        VerticalAlignment.CENTER,
        doorOrdersLength ? this.commonGroupCellClass : css.groupedCell
      ),
      cellElement: IADoorLevelQuantitiesCell,
      cellAlignment: false,
      hierarchyRowCellTemplate: IADoorLevelHierarchyCell
    } as Column
  }

  getWareHouseDoor(quantityKPIS) {
    return {
      field: 'IAWarehouse',
      label: 'Warehouse',
      width: commonColumnWidth,
      allowHide: false,
      showBorders: false,
      allowReordering: false,
      headerCellElement: withAlignment(
        IAWarehouseHeaderWithKPI({
          headers: quantityKPIS
        }),
        Alignment.LEFT,
        VerticalAlignment.CENTER,
        css.groupedCell
      ),
      cellElement: IAWarehouseQuantitiesCell,
      cellAlignment: false,
      hierarchyRowCellTemplate: withCustomField(IASingleValueHeirarchyDetailsCell, {
        column: {
          label: '',
          initialAllocationField: 'totalDeliveryWarehouseSizeQuantity'
        }
      })
    } as Column
  }

  getColumns: (
    doorOrders: any[],
    groupedDoors?: boolean,
    captureSize?: boolean,
    isValueSelected?: boolean,
    quantityType?: string,
    productInfoColumnName?: string,
    hiddenTotalKpiRows?: any[],
    tableSettingRows?: any
  ) => Column[] = (
    doorOrders,
    groupedDoors,
    captureSize,
    isValueSelected,
    quantityType,
    productInfoColumnName,
    hiddenTotalKpiRows = [],
    tableSettingRows = {}
  ) => {
    const hideClusterNameRow =
      config.assortmentSource.retail === ASSORTMENT_SOURCE.CLUSTER_GROUP ||
      hiddenTotalKpiRows?.includes('clusterName')

    const nameColumn = {
      field: 'name',
      width: 375,
      label: `${productInfoColumnName}`,
      key: '',
      fixed: true,
      showHeader: true,
      allowHide: false,
      showBorders: true,
      cellElement: CNFProductInfo,
      className: css.headerPosition,
      headerCellElement: withAlignment(
        HeaderCell,
        Alignment.LEFT,
        VerticalAlignment.CENTER,
        css['enhancedCellAlignment']
      ),
      hierarchyRowCellTemplate: withAlignment(
        EnhancedListHierarchyHeader,
        Alignment.LEFT,
        VerticalAlignment.CENTER
      )
    }

    const quantityColumn = {
      field: 'deliveryProductQuantity',
      enhancedField: 'clientOrderProductQuantity',
      initialAllocationField: 'deliveryProductQuantity',
      width: commonColumnWidth,
      label: 'Product Quantity',
      key: 'quantity.totalByProductQuantity',
      attributeId: 'totalProductQuantity',
      showBorders: true,
      fixed: true,
      allowHide: false,
      className: css.headerPosition,
      headerCellElement: withAlignment(
        HeaderCell, // change to rollup all button while implementation
        Alignment.CENTER,
        VerticalAlignment.CENTER,
        css['enhancedCellAlignment']
      ),
      cellElement: IAProductQuantityCell,
      cellAlignment: false,
      hierarchyRowCellTemplate: withCustomField(IAProductHeirarchyDetailsCell, {
        column: {
          enhancedField: 'totalProductQuantity',
          label: '',
          initialAllocationField: 'totalDeliveryProductQuantity'
        }
      })
    }

    const kpiLabels = getQuantityKPILabels(
      quantityType,
      VIPChannel.Retail,
      hiddenTotalKpiRows,
      tableSettingRows
    )

    const doorQuantityColunn = {
      field: 'deliveryAllocatedProductQuantity',
      width: commonColumnWidth,
      initialAllocationField: 'deliveryAllocatedProductQuantity',
      label: 'Door QTY',
      key: 'doorQTY',
      fixed: true,
      allowHide: false,
      attributeId: 'totalDoorQuantity',
      showBorders: false,
      isStickyBorder: true,
      cellElement: IADoorQuantityCell,
      headerCellElement: withAlignment(
        QuantityHeaderWithKPI({ headers: kpiLabels, doorOrderId: null }),
        Alignment.LEFT,
        VerticalAlignment.CENTER,
        this.commonGroupCellClass
      ),
      cellAlignment: false,
      hierarchyRowCellTemplate: withCustomField(IAProductHeirarchyDetailsCell, {
        column: {
          enhancedField: 'totalDoorQuantity',
          label: '',
          initialAllocationField: 'totalDeliveryDoorQuantity'
        }
      })
    }

    const quantityKPIS = getQuantityKPIColumns(
      quantityType,
      VIPChannel.Retail,
      hiddenTotalKpiRows,
      tableSettingRows
    )

    const clusteredDoorColumns = this.getClusteredDoors(
      doorOrders,
      quantityKPIS,
      hideClusterNameRow
    )
    let warehouseColumn = []
    if (captureSize) {
      // When assortment source is cluster group, hiding warehouse row.
      if (hideClusterNameRow) {
        warehouseColumn = [this.getWareHouseDoor(quantityKPIS) as Column]
      } else {
        warehouseColumn = [
          {
            field: 'IAWarehouse',
            label: 'Warehouse',
            filterSubColumns: true,
            showHeader: true,
            allowHide: false,
            showBorders: true,
            allowReordering: false,
            type: ColumnType.Grouped,
            columns: [this.getWareHouseDoor(quantityKPIS) as Column]
          }
        ]
      }
    }

    return [
      nameColumn,
      quantityColumn,
      doorQuantityColunn,
      ...warehouseColumn,
      ...clusteredDoorColumns
    ] as Column[]
  }
}
